import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Package,
  Truck,
  PackageCheck,
  CheckCircle2,
  Ban,
  ShoppingBag,
  Loader2,
  RefreshCcw,
  RotateCcw,
  Clock3,
  BadgeCheck,
  XCircle,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { apiListMyOrders, apiListMyReturns } from "@/lib/api";
import { LazyImage } from "@/components/LazyImage";
import { ReturnOrderDialog } from "@/components/ReturnOrderDialog";

const FLOW = ["Placed", "Packed", "Shipped", "Delivered"];
const STEP_ICONS = {
  Placed: PackageCheck,
  Packed: Package,
  Shipped: Truck,
  Delivered: CheckCircle2,
};

const formatINR = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const MyOrdersDialog = ({ open, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [returnsByOrder, setReturnsByOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [returnFor, setReturnFor] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      // Fetch orders + the customer's returns in parallel so we can render
      // a live status line instead of the Return button once a request exists.
      const [list, myReturns] = await Promise.all([
        apiListMyOrders(),
        apiListMyReturns().catch(() => []),
      ]);
      setOrders(Array.isArray(list) ? list : []);
      const map = {};
      (Array.isArray(myReturns) ? myReturns : []).forEach((r) => {
        const key = r.order_id_or_phone;
        if (!key) return;
        // Keep the most-recent return per order (list arrives newest-first).
        if (!map[key]) map[key] = r;
      });
      setReturnsByOrder(map);
    } catch (e) {
      toast.error(e?.message || "Couldn't load your orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) reload();
  }, [open]);

  return (
    <>
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-black/55 backdrop-blur-sm"
          onClick={onClose}
          data-testid="my-orders-dialog"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-3xl bg-white sm:rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.22)] overflow-hidden max-h-[100vh] sm:max-h-[92vh] flex flex-col"
          >
            <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E0B53C]/70 to-transparent" />
            <button
              onClick={onClose}
              data-testid="my-orders-close"
              aria-label="close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-neutral-100 flex items-center justify-center shadow-sm border border-black/5"
            >
              <X size={16} strokeWidth={1.8} />
            </button>

            <header className="px-6 sm:px-8 pt-7 pb-4 flex items-end justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#064E3B] font-semibold">
                  Your orders
                </p>
                <h2 className="font-display text-3xl sm:text-4xl text-[#0B0C10] font-light tracking-tight">
                  Track every parcel.
                </h2>
              </div>
              <button
                onClick={reload}
                disabled={loading}
                data-testid="my-orders-refresh"
                aria-label="refresh"
                className="inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-[#0B0C10] disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 size={13} strokeWidth={2} className="animate-spin" />
                ) : (
                  <RefreshCcw size={13} strokeWidth={2} />
                )}
                Refresh
              </button>
            </header>

            <div className="overflow-y-auto px-6 sm:px-8 pb-8 space-y-3">
              {loading && orders.length === 0 ? (
                <SkeletonState />
              ) : orders.length === 0 ? (
                <EmptyState onClose={onClose} />
              ) : (
                orders.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    returnRequest={returnsByOrder[o.id] || null}
                    onRequestReturn={() => setReturnFor(o)}
                  />
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    <ReturnOrderDialog
      open={!!returnFor}
      initialOrder={returnFor}
      onClose={() => setReturnFor(null)}
      onSubmitted={() => {
        // Refresh returns list in the background so the pill is ready by
        // the time the user closes the success screen. Don't close the
        // dialog here — the success view + Done button handle that.
        reload();
      }}
    />
    </>
  );
};

const RETURN_STATUS_STYLE = {
  Pending: {
    label: "Return Requested",
    Icon: Clock3,
    className: "bg-[#FFF9E6] text-[#7A5A00] border-[#E0B53C]/40",
  },
  Approved: {
    label: "Return Approved",
    Icon: BadgeCheck,
    className: "bg-[#D1FFEB] text-[#064E3B] border-[#064E3B]/25",
  },
  Rejected: {
    label: "Return Rejected",
    Icon: XCircle,
    className: "bg-red-50 text-red-600 border-red-200",
  },
  Refunded: {
    label: "Refund Processed",
    Icon: Wallet,
    className: "bg-[#D1FFEB] text-[#064E3B] border-[#064E3B]/25",
  },
};

const OrderCard = ({ order, returnRequest, onRequestReturn }) => {
  const status = order.status || "Placed";
  const cancelled = status === "Cancelled";
  const delivered = status === "Delivered";
  const curIdx = FLOW.indexOf(status);
  // 5-day window: use delivered_at if backend supplies it, else fall back to
  // the order's createdAt. Button disappears once the 5-day window elapses.
  const deliveredAt = order.delivered_at || order.deliveredAt || order.createdAt;
  const withinReturnWindow =
    delivered &&
    deliveredAt &&
    Date.now() - new Date(deliveredAt).getTime() <= 5 * 24 * 60 * 60 * 1000;

  // Once a return request exists for this order, render a live status pill
  // (fetched from /api/returns/mine) INSTEAD of the Return CTA so customers
  // can track it. Falls back to Pending styling if the status is unknown.
  const rStatus =
    returnRequest &&
    (RETURN_STATUS_STYLE[returnRequest.status] ||
      RETURN_STATUS_STYLE.Pending);

  return (
    <article
      data-testid={`my-order-${order.id}`}
      className="rounded-2xl border border-black/5 bg-white p-4 sm:p-5 shadow-[0_4px_18px_rgba(0,0,0,0.04)] space-y-4"
    >
      <header className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-100 shrink-0 relative ring-1 ring-black/5">
          <LazyImage
            src={order.productImage}
            alt={order.productTitle}
            wrapperClassName="w-full h-full"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-neutral-500">
              #{String(order.id).slice(0, 8).toUpperCase()}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
              {order.payment || "COD"}
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full font-medium ${
                cancelled
                  ? "bg-red-50 text-red-600"
                  : status === "Delivered"
                    ? "bg-[#D1FFEB] text-[#064E3B]"
                    : "bg-[#FFF9E6] text-[#064E3B]"
              }`}
              data-testid={`my-order-status-${order.id}`}
            >
              {status}
            </span>
          </div>
          <h3 className="font-display text-base sm:text-lg text-[#0B0C10] truncate">
            {order.productTitle}
          </h3>
          {Array.isArray(order.items) && order.items.length > 0 && (
            <p className="text-[11px] text-neutral-600 uppercase tracking-[0.14em] truncate">
              {order.items
                .map(
                  (it) =>
                    `${it.title} · Size ${it.size}${it.colorLabel ? ` · ${it.colorLabel}` : ""} × ${it.qty}`,
                )
                .join("  ·  ")}
            </p>
          )}
          <p className="text-[11px] text-neutral-500 mt-1">
            Placed{" "}
            {new Date(order.createdAt).toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </p>
        </div>
        <p className="font-display text-xl text-[#0B0C10] shrink-0">
          {formatINR(order.price ?? order.totalPrice ?? 0)}
        </p>
      </header>

      {/* Pipeline */}
      <ol
        className="flex items-center gap-1 sm:gap-2 overflow-x-auto"
        aria-label="order status timeline"
      >
        {cancelled ? (
          <li className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium bg-red-50 text-red-600">
            <Ban size={12} strokeWidth={2} />
            Cancelled
          </li>
        ) : (
          FLOW.map((step, i) => {
            const reached = curIdx >= i;
            const Icon = STEP_ICONS[step] || Package;
            return (
              <React.Fragment key={step}>
                <li
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium whitespace-nowrap transition-colors ${
                    reached
                      ? "bg-[#D1FFEB] text-[#064E3B]"
                      : "bg-neutral-100 text-neutral-400"
                  }`}
                >
                  <Icon size={12} strokeWidth={2} />
                  <span>{step}</span>
                </li>
                {i < FLOW.length - 1 && (
                  <span
                    className={`h-px flex-1 min-w-3 ${
                      curIdx > i ? "bg-[#064E3B]/30" : "bg-neutral-200"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })
        )}
      </ol>

      {order.tracking_number && status === "Shipped" && (
        <p className="text-[11px] text-neutral-600">
          Tracking:{" "}
          <span className="font-mono text-[#0B0C10]">
            {order.tracking_number}
          </span>
        </p>
      )}
      {cancelled && order.cancel_reason && (
        <p className="text-[11px] text-red-500">
          Reason: {order.cancel_reason}
        </p>
      )}

      <p className="text-[11px] text-neutral-500">
        Delivering to {order.house}, {order.colony}, {order.city},{" "}
        {order.state} — {order.pincode}
      </p>

      {/* Return status — once a request exists we replace the CTA with a
          live status line fetched from /api/returns/mine. Otherwise fall
          through to the Return button (visible during the 5-day window). */}
      {rStatus ? (
        <div
          className={`inline-flex items-center gap-1.5 self-start text-[11px] font-medium rounded-full border px-3 py-1.5 ${rStatus.className}`}
          data-testid={`my-order-return-status-${order.id}`}
        >
          <rStatus.Icon size={12} strokeWidth={2} />
          <span className="uppercase tracking-[0.14em]">{rStatus.label}</span>
          {returnRequest?.created_at && (
            <span className="normal-case tracking-normal opacity-70 ml-1">
              ·{" "}
              {new Date(returnRequest.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
              })}
            </span>
          )}
        </div>
      ) : withinReturnWindow ? (
        <button
          type="button"
          onClick={onRequestReturn}
          data-testid={`my-order-return-btn-${order.id}`}
          className="inline-flex items-center gap-1.5 self-start text-xs font-medium rounded-full border border-[#064E3B]/30 bg-white text-[#064E3B] px-3 py-1.5 hover:bg-[#064E3B] hover:text-white hover:border-[#064E3B] transition-colors"
        >
          <RotateCcw size={12} strokeWidth={1.8} />
          Return Product
        </button>
      ) : delivered ? (
        <p className="text-[11px] text-neutral-500 self-start">
          5-day return window has ended.
        </p>
      ) : null}
    </article>
  );
};

const SkeletonState = () => (
  <div className="space-y-3" data-testid="my-orders-loading">
    {Array.from({ length: 2 }).map((_, i) => (
      <div
        key={i}
        className="rounded-2xl border border-black/5 bg-white p-5 space-y-3 animate-pulse"
      >
        <div className="h-4 w-1/3 bg-neutral-100 rounded" />
        <div className="h-6 w-2/3 bg-neutral-100 rounded" />
        <div className="h-3 w-1/2 bg-neutral-100 rounded" />
      </div>
    ))}
  </div>
);

const EmptyState = ({ onClose }) => (
  <div
    data-testid="my-orders-empty"
    className="rounded-2xl border border-black/5 bg-white p-10 text-center"
  >
    <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF9E6] flex items-center justify-center mb-4">
      <ShoppingBag size={22} strokeWidth={1.6} className="text-[#064E3B]" />
    </div>
    <h3 className="font-display text-xl text-[#0B0C10] mb-1">
      No orders yet.
    </h3>
    <p className="text-sm text-neutral-600 mb-5">
      Place your first MastiBazaar order and track it here.
    </p>
    <button
      onClick={onClose}
      className="inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
    >
      Start shopping
    </button>
  </div>
);
